import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useAuth } from "../../../contexts/AuthContext";
import { TerminosModal } from "./components/TerminosModal";

const registerSchema = z
  .object({
    nombres: z.string().min(2, "Los nombres deben tener al menos 2 caracteres"),
    apellidos: z
      .string()
      .min(2, "Los apellidos deben tener al menos 2 caracteres"),
    email: z.string().email("Email inválido"),
    password: z
      .string()
      .min(6, "La contraseña debe tener al menos 6 caracteres"),
    confirmPassword: z.string(),
    dia: z.string().min(1, "El día es requerido"),
    mes: z.string().min(1, "El mes es requerido"),
    anio: z.string().min(1, "El año es requerido"),
    fechaNacimiento: z.string().optional(),
    rut: z.string().optional(),
    esMayor14: z.boolean().refine((val) => val === true, {
      message: "Debes ser mayor de 14 años",
    }),
    aceptoTerminos: z.boolean().refine((val) => val === true, {
      message: "Debes aceptar los términos y condiciones",
    }),
    rol: z
      .string()
      .refine((val) => val === "ALUMNO" || val === "PROFESOR" || !val, {
        message: "Debe ser ALUMNO o PROFESOR",
      })
      .optional(),
    codigoAutorizacion: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  })
  .refine(
    (data) => {
      if (data.rol === "PROFESOR" && !data.codigoAutorizacion) {
        return false;
      }
      return true;
    },
    {
      message: "El código de autorización es requerido para profesores",
      path: ["codigoAutorizacion"],
    }
  )
  .refine(
    (data) => {
      if (data.dia && data.mes && data.anio) {
        const fecha = new Date(
          parseInt(data.anio),
          parseInt(data.mes) - 1,
          parseInt(data.dia)
        );
        const hoy = new Date();
        const edad = hoy.getFullYear() - fecha.getFullYear();
        const mesDiff = hoy.getMonth() - fecha.getMonth();
        if (mesDiff < 0 || (mesDiff === 0 && hoy.getDate() < fecha.getDate())) {
          return edad - 1 >= 14;
        }
        return edad >= 14;
      }
      return true;
    },
    {
      message: "Debes ser mayor de 14 años",
      path: ["esMayor14"],
    }
  );

type RegisterFormData = z.infer<typeof registerSchema>;

export const RegisterPage: React.FC = () => {
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showTerminosModal, setShowTerminosModal] = useState(false);
  const [showPrivacidadModal, setShowPrivacidadModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      esMayor14: false,
      aceptoTerminos: false,
      rol: "ALUMNO",
    },
  });

  const selectedRol = watch("rol");
  const dia = watch("dia");
  const mes = watch("mes");
  const anio = watch("anio");

  // Actualizar fechaNacimiento cuando cambien día, mes o año
  React.useEffect(() => {
    if (dia && mes && anio) {
      const fechaFormateada = `${anio}-${mes}-${dia}`;
      setValue("fechaNacimiento", fechaFormateada);

      // Validar edad automáticamente
      const fecha = new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));
      const hoy = new Date();
      const edad = hoy.getFullYear() - fecha.getFullYear();
      const mesDiff = hoy.getMonth() - fecha.getMonth();
      const edadCalculada =
        mesDiff < 0 || (mesDiff === 0 && hoy.getDate() < fecha.getDate())
          ? edad - 1
          : edad;

      setValue("esMayor14", edadCalculada >= 14);
    }
  }, [dia, mes, anio, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError(null);

      // Construir fecha de nacimiento desde día, mes y año
      const fechaNacimiento = `${data.anio}-${data.mes}-${data.dia}`;

      // Preparar datos para el backend
      const payload: any = {
        nombres: data.nombres,
        apellidos: data.apellidos,
        email: data.email,
        password: data.password,
        fechaNacimiento: fechaNacimiento,
        esMayor14: data.esMayor14,
        aceptoTerminos: data.aceptoTerminos,
      };

      if (data.rut) {
        payload.rut = data.rut;
      }

      if (data.rol === "PROFESOR") {
        payload.rol = "PROFESOR";
        payload.codigoAutorizacion = data.codigoAutorizacion;
      }

      const result = await registerUser(payload);

      if (result.status === "success") {
        setSuccess(true);
        // Redirigir después de 2 segundos
        setTimeout(() => {
          if (result.data?.usuario.rol === "ADMIN") {
            window.location.href = "/admin";
          } else {
            window.location.href = "/unauthorized";
          }
        }, 2000);
      } else {
        setError(result.message || "Error al registrar usuario");
      }
    } catch (error: any) {
      setError(error.message || "Error al registrar usuario");
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8 text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Registro exitoso!
          </h2>
          <p className="text-gray-600 mb-4">
            Se ha enviado un correo de validación a tu email. Por favor verifica
            tu cuenta antes de iniciar sesión.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            Ir al inicio de sesión
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Fracto
          </h2>
          <p className="mt-2 text-sm text-gray-600">Crear cuenta</p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombres" className="text-gray-700 font-medium">
                  Nombres *
                </Label>
                <Input
                  id="nombres"
                  type="text"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="Juan"
                  {...register("nombres")}
                />
                {errors.nombres && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.nombres.message}
                  </p>
                )}
              </div>

              <div>
                <Label
                  htmlFor="apellidos"
                  className="text-gray-700 font-medium"
                >
                  Apellidos *
                </Label>
                <Input
                  id="apellidos"
                  type="text"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="Pérez"
                  {...register("apellidos")}
                />
                {errors.apellidos && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.apellidos.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-gray-700 font-medium">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="tu@email.com"
                {...register("email")}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="rut" className="text-gray-700 font-medium">
                RUT (Opcional)
              </Label>
              <Input
                id="rut"
                type="text"
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="12345678-9"
                {...register("rut")}
              />
              {errors.rut && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.rut.message}
                </p>
              )}
            </div>

            <div>
              <Label className="text-gray-700 font-medium block mb-2">
                Fecha de Nacimiento *
              </Label>
              <div className="grid grid-cols-3 gap-3">
                {/* Día */}
                <div>
                  <select
                    id="dia"
                    className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-700"
                    {...register("dia", {
                      required: "El día es requerido",
                    })}
                  >
                    <option value="">Día</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((dia) => (
                      <option key={dia} value={dia.toString().padStart(2, "0")}>
                        {dia}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mes */}
                <div>
                  <select
                    id="mes"
                    className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-700"
                    {...register("mes", {
                      required: "El mes es requerido",
                    })}
                  >
                    <option value="">Mes</option>
                    {[
                      "Enero",
                      "Febrero",
                      "Marzo",
                      "Abril",
                      "Mayo",
                      "Junio",
                      "Julio",
                      "Agosto",
                      "Septiembre",
                      "Octubre",
                      "Noviembre",
                      "Diciembre",
                    ].map((mes, index) => (
                      <option
                        key={mes}
                        value={(index + 1).toString().padStart(2, "0")}
                      >
                        {mes}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Año */}
                <div>
                  <select
                    id="anio"
                    className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm text-gray-700"
                    {...register("anio", {
                      required: "El año es requerido",
                    })}
                  >
                    <option value="">Año</option>
                    {Array.from(
                      { length: 100 },
                      (_, i) => new Date().getFullYear() - i
                    ).map((anio) => (
                      <option key={anio} value={anio}>
                        {anio}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {errors.dia && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.dia.message as string}
                </p>
              )}
              {errors.mes && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.mes.message as string}
                </p>
              )}
              {errors.anio && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.anio.message as string}
                </p>
              )}
              {errors.fechaNacimiento && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.fechaNacimiento.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="rol" className="text-gray-700 font-medium">
                Tipo de Usuario *
              </Label>
              <select
                id="rol"
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                {...register("rol")}
              >
                <option value="ALUMNO">Alumno</option>
                <option value="PROFESOR">Profesor</option>
              </select>
              {errors.rol && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.rol.message as string}
                </p>
              )}
            </div>

            {selectedRol === "PROFESOR" && (
              <div>
                <Label
                  htmlFor="codigoAutorizacion"
                  className="text-gray-700 font-medium"
                >
                  Código de Autorización *
                </Label>
                <Input
                  id="codigoAutorizacion"
                  type="text"
                  required
                  className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  placeholder="PROF-2024-001"
                  {...register("codigoAutorizacion")}
                />
                {errors.codigoAutorizacion && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.codigoAutorizacion.message}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Necesitas un código de autorización para registrarte como
                  profesor. Contacta al administrador de tu institución para
                  obtenerlo.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-gray-700 font-medium">
                Contraseña *
              </Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="Mínimo 6 caracteres"
                {...register("password")}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="confirmPassword"
                className="text-gray-700 font-medium"
              >
                Confirmar Contraseña *
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                placeholder="Repite tu contraseña"
                {...register("confirmPassword")}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="esMayor14"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register("esMayor14")}
                />
                <label
                  htmlFor="esMayor14"
                  className="ml-2 text-sm text-gray-700"
                >
                  Confirmo que soy mayor de 14 años *
                </label>
              </div>
              {errors.esMayor14 && (
                <p className="text-sm text-red-600">
                  {errors.esMayor14.message}
                </p>
              )}

              <div className="flex items-start">
                <input
                  id="aceptoTerminos"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register("aceptoTerminos")}
                />
                <label
                  htmlFor="aceptoTerminos"
                  className="ml-2 text-sm text-gray-700"
                >
                  Acepto los{" "}
                  <button
                    type="button"
                    onClick={() => setShowTerminosModal(true)}
                    className="text-blue-600 hover:underline"
                  >
                    términos y condiciones
                  </button>{" "}
                  y la{" "}
                  <button
                    type="button"
                    onClick={() => setShowPrivacidadModal(true)}
                    className="text-blue-600 hover:underline"
                  >
                    política de privacidad
                  </button>{" "}
                  *
                </label>
              </div>
              {errors.aceptoTerminos && (
                <p className="text-sm text-red-600">
                  {errors.aceptoTerminos.message}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200 transform hover:scale-105"
              >
                {isSubmitting ? "Registrando..." : "Registrarse"}
              </Button>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                ¿Ya tienes una cuenta?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/auth")}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200"
                >
                  Inicia sesión aquí
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Modales de Términos y Privacidad */}
      <TerminosModal
        isOpen={showTerminosModal}
        onClose={() => setShowTerminosModal(false)}
        tipo="terminos"
      />
      <TerminosModal
        isOpen={showPrivacidadModal}
        onClose={() => setShowPrivacidadModal(false)}
        tipo="privacidad"
      />
    </div>
  );
};
